import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import SchoolIcon from "@mui/icons-material/School";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import { useBrand } from "../context/BrandContext";
import { usePermissions } from "../context/Permissioncontext";
import { resolveClubImageUrl } from "../utils/resolveClubImageUrl";
import { userCanManageTrainingSessions } from "../utils/planningAccess";
import { isPlayerAccount, readActiveHomeRole } from "../utils/homeRole";
import "./GroupMembersOverviewDialog.css";

function getPersonName(person, user, t) {
  if (user?.user_alias) return user.user_alias;
  if (user?.user_first_name && user?.user_last_name) {
    return `${user.user_first_name} ${user.user_last_name}`.trim();
  }
  return `${t("person")} #${person.player_id || person.coach_id || ""}`.slice(-4);
}

function getPersonInitials(person, user) {
  if (user?.user_alias) return user.user_alias.slice(0, 2).toUpperCase();
  if (user?.user_first_name && user?.user_last_name) {
    return (user.user_first_name[0] + user.user_last_name[0]).toUpperCase();
  }
  if (user?.user_first_name) return user.user_first_name.slice(0, 2).toUpperCase();
  return "P";
}

function GoalList({ items, emptyLabel, secondary = false, t }) {
  if (!items?.length) {
    return <p className="gmd-dlg__muted">{emptyLabel}</p>;
  }
  return (
    <>
      {items.map((item, index) => (
        <div key={`${secondary ? "s" : "p"}-${item.attribute_id}`} className="gmd-dlg__goal">
          <span className="gmd-dlg__goal-rank">{index + 1}</span>
          <span className="gmd-dlg__goal-label">{item.label}</span>
          <span
            className="gmd-dlg__goal-count"
            title={t("groupObjectiveCountTooltip", { count: item.count })}
          >
            {item.count}
          </span>
        </div>
      ))}
    </>
  );
}

function PersonCard({ person, role, t }) {
  const user = person.tbl_user || {};
  const name = getPersonName(person, user, t);
  const isCoach = role === "coach";

  return (
    <article className={`gmd-dlg__person${isCoach ? " gmd-dlg__person--coach" : ""}`}>
      {user.user_image ? (
        <img className="gmd-dlg__person-avatar" src={user.user_image} alt="" />
      ) : (
        <div className="gmd-dlg__person-avatar gmd-dlg__person-avatar--fallback">
          {getPersonInitials(person, user)}
        </div>
      )}
      <div className="gmd-dlg__person-body">
        <p className="gmd-dlg__person-name" title={name}>
          {name}
        </p>
        <div className="gmd-dlg__person-meta">
          <span className={`gmd-dlg__badge${isCoach ? " gmd-dlg__badge--coach" : ""}`}>
            {isCoach ? t("coach") : t("player")}
          </span>
          {!isCoach && (person.fltt_classement || person.fltt_ranking_points != null) && (
            <span className="gmd-dlg__classement">
              {[
                person.fltt_classement,
                person.fltt_ranking_points != null
                  ? `${parseFloat(person.fltt_ranking_points).toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })} ${t("points")}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * Homepage group card overview: goals, coaches, players.
 */
export default function GroupMembersOverviewDialog({
  open,
  onClose,
  group,
  members,
  loading = false,
  availableGroups = [],
}) {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { hasPermission, permissions } = usePermissions();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const homeRole = readActiveHomeRole(user);
  const canAddSession =
    !isPlayerAccount(user, { homeRole }) &&
    userCanManageTrainingSessions(user, permissions, hasPermission);
  const groupDetails = group?.groupDetails || group?.group || group || {};
  const name = groupDetails.gr_name || t("group");
  const image = groupDetails.gr_image || null;
  const clubName = brand?.brand_name || t("club");
  const clubLogo = resolveClubImageUrl(brand?.logo_url);
  const coaches = members?.coaches || [];
  const players = members?.players || [];
  const primary = members?.objectiveSummary?.primary || [];
  const secondary = members?.objectiveSummary?.secondary || [];
  const noGoals = primary.length === 0 && secondary.length === 0;

  const handleAddSession = useCallback(() => {
    if (!canAddSession || !group) return;
    window.dispatchEvent(
      new CustomEvent("openAddSessionDialog", {
        detail: {
          selectedgrp: group,
          availableGroups: Array.isArray(availableGroups) ? availableGroups : [],
        },
        bubbles: true,
        cancelable: true,
      })
    );
    onClose?.();
  }, [availableGroups, canAddSession, group, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="gmd-dlg gmd-dlg--compact"
      PaperProps={{ className: "gmd-dlg-paper" }}
      aria-labelledby="gmd-bar-title"
    >
      <header className="gmd-bar">
        <div className="gmd-bar__id">
          {image ? (
            <img className="gmd-bar__avatar" src={image} alt="" />
          ) : (
            <div className="gmd-bar__avatar gmd-bar__avatar--fallback">
              {(name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="gmd-bar__text">
            <p className="gmd-bar__club">
              {clubLogo ? <img src={clubLogo} alt="" /> : null}
              <span>{clubName}</span>
            </p>
            <h2 id="gmd-bar-title" className="gmd-bar__title" title={name}>
              {name}
            </h2>
          </div>
        </div>

        <dl className="gmd-bar__tally">
          <div className="gmd-bar__tally-cell">
            <dt>{t("coaches")}</dt>
            <dd>{coaches.length}</dd>
          </div>
          <div className="gmd-bar__tally-cell">
            <dt>{t("players")}</dt>
            <dd>{players.length}</dd>
          </div>
          <div className="gmd-bar__tally-cell gmd-bar__tally-cell--muted">
            <dt>{t("groupDialogGoalsStat")}</dt>
            <dd>{primary.length + secondary.length}</dd>
          </div>
        </dl>

        <div className="gmd-dlg__header-actions">
          {canAddSession ? (
            <IconButton
              className="gmd-dlg__add-session"
              onClick={handleAddSession}
              size="small"
              aria-label={t("addsession")}
              title={t("addsession")}
              data-tour-id="group-dialog-add-session"
            >
              <EventAvailableOutlinedIcon fontSize="small" />
            </IconButton>
          ) : null}
          <IconButton
            className="gmd-dlg__close"
            onClick={onClose}
            size="small"
            aria-label={t("close")}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </header>

      <DialogContent className="gmd-dlg__body">
        {loading ? (
          <div className="gmd-dlg__loading">
            <CircularProgress size={28} />
            <span>{t("loading")}</span>
          </div>
        ) : (
          <div className="gmd-dlg__scroll">
            <section className="gmd-dlg__section">
              <div className="gmd-dlg__section-head">
                <h3 className="gmd-dlg__section-title">
                  <FlagIcon />
                  {t("groupDialogGoalsHeading")}
                </h3>
              </div>
              <div className="gmd-dlg__goals-grid">
                <div className="gmd-dlg__goals-col">
                  <p className="gmd-dlg__goals-col-title">{t("groupDialogTopPrimary")}</p>
                  <GoalList
                    items={primary}
                    emptyLabel={t("groupDialogNoPrimary")}
                    t={t}
                  />
                </div>
                <div className="gmd-dlg__goals-col gmd-dlg__goals-col--secondary">
                  <p className="gmd-dlg__goals-col-title">{t("groupDialogTopSecondary")}</p>
                  <GoalList
                    items={secondary}
                    emptyLabel={t("groupDialogNoSecondary")}
                    secondary
                    t={t}
                  />
                </div>
              </div>
              {noGoals && players.length > 0 && (
                <p className="gmd-dlg__hint">{t("groupDialogNoGoalsHint")}</p>
              )}
            </section>

            {coaches.length > 0 && (
              <section className="gmd-dlg__section">
                <div className="gmd-dlg__section-head">
                  <h3 className="gmd-dlg__section-title">
                    <SchoolIcon />
                    {t("coaches")}
                  </h3>
                  <span className="gmd-dlg__count">{coaches.length}</span>
                </div>
                <div className="gmd-dlg__people">
                  {coaches.map((coach) => (
                    <PersonCard
                      key={coach.coach_id}
                      person={coach}
                      role="coach"
                      t={t}
                    />
                  ))}
                </div>
              </section>
            )}

            {players.length > 0 && (
              <section className="gmd-dlg__section">
                <div className="gmd-dlg__section-head">
                  <h3 className="gmd-dlg__section-title">
                    <PersonIcon />
                    {t("players")}
                  </h3>
                  <span className="gmd-dlg__count">{players.length}</span>
                </div>
                <div className="gmd-dlg__people">
                  {players.map((player) => (
                    <PersonCard
                      key={player.player_id}
                      person={player}
                      role="player"
                      t={t}
                    />
                  ))}
                </div>
              </section>
            )}

            {players.length === 0 && coaches.length === 0 && !loading && (
              <div className="gmd-dlg__empty">
                <GroupsIcon sx={{ fontSize: 40, opacity: 0.35 }} />
                <p>{t("noMembersInGroup")}</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
